import config from '../config'
import { clearTokens, getToken, refreshTokenIfNeeded } from './auth'
import { resolveLocale } from '../i18n/locale'

const BASE_URL = config.API_URL
const LEGACY_BASE_URL = config.LEGACY_API_URL
const stagedUploads = new Map()

function splitRequirementText(value) {
  return String(value || '')
    .split(/\r?\n|,|;/)
    .map((item) => item.replace(/^[-*\u2022]\s*/, '').trim())
    .filter(Boolean)
}

function prettifyFileName(fileName) {
  const base = String(fileName || '')
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!base) return 'Candidate'
  return base.replace(/\b\w/g, (char) => char.toUpperCase())
}

function normaliseStatus(status) {
  return status || 'pending'
}

function buildFallbackDescription(jobTitle, jobDescription) {
  const title = String(jobTitle || '').trim() || 'Uploaded role'
  const description = String(jobDescription || '').trim()
  if (description.length >= 20) {
    return description
  }

  return `Candidate screening for ${title}. Review relevant skills, experience, and overall professional fit for this role.`
}

function buildJobDescription(jobData) {
  const title = String(jobData?.title || jobData?.jobTitle || '').trim() || 'Uploaded role'
  const description = buildFallbackDescription(
    title,
    jobData?.description || jobData?.jobDescription
  )
  const extras = []

  if (String(jobData?.requirements || '').trim()) {
    extras.push(`Requirements:\n${String(jobData.requirements).trim()}`)
  }
  if (String(jobData?.location || '').trim()) {
    extras.push(`Location: ${String(jobData.location).trim()}`)
  }
  if (String(jobData?.employmentType || '').trim()) {
    extras.push(`Employment type: ${String(jobData.employmentType).trim()}`)
  }

  return extras.length ? `${description}\n\n${extras.join('\n\n')}` : description
}

function buildMissingSkills(jobTerms = [], matchedTerms = []) {
  const matched = new Set(matchedTerms.map((term) => String(term).toLowerCase()))
  return jobTerms.filter((term) => !matched.has(String(term).toLowerCase())).slice(0, 6)
}

function buildStrengths(candidate) {
  const breakdown = candidate.scoreBreakdown || candidate.score_breakdown || {}
  const aiEvaluation = breakdown.aiEvaluation || breakdown.ai_evaluation || candidate.aiEvaluation || candidate.ai_evaluation
  if (aiEvaluation?.explanation) {
    return [aiEvaluation.explanation].slice(0, 3)
  }
  const strengths = [
    breakdown.skill_match?.explanation,
    breakdown.must_have?.explanation,
    breakdown.experience?.explanation,
    breakdown.rule_score?.explanation,
    breakdown.semantic_score?.explanation,
    breakdown.coreDomain?.explanation,
    breakdown.mustHave?.explanation,
    breakdown.roleTitle?.explanation,
    breakdown.responsibility?.explanation,
    breakdown.seniority?.explanation,
    breakdown.toolPlatform?.explanation,
    breakdown.geoRegulatory?.explanation,
    breakdown.credential?.explanation,
    breakdown.penalty?.explanation,
    candidate.score_breakdown?.rule_score?.explanation,
    candidate.score_breakdown?.experience?.explanation,
    candidate.score_breakdown?.semantic_score?.explanation,
  ]

  return strengths
    .filter((value) => typeof value === 'string' && value.trim())
    .slice(0, 3)
}

function mapCandidateScoreBreakdown(candidate) {
  return candidate.scoreBreakdown || candidate.score_breakdown || null
}

function buildWhyThisScore(candidate) {
  const breakdown = candidate.scoreBreakdown || candidate.score_breakdown || {}
  const aiEvaluation =
    breakdown.aiEvaluation ||
    breakdown.ai_evaluation ||
    candidate.aiEvaluation ||
    candidate.ai_evaluation ||
    null
  if (aiEvaluation?.explanation) {
    return aiEvaluation.explanation
  }

  return (
    candidate.whyThisScore ||
    candidate.whyThisMatch ||
    candidate.explanation ||
    candidate.summary ||
    ''
  )
}

function mapJobRecord(job) {
  return {
    jobId: job.jobId || job.job_id,
    title: job.title || job.jobTitle || '',
    description: job.description || job.jobDescription || '',
    requirements: job.requirements || job.requirement || '',
    requiredSkills: job.requiredSkills || job.required_skills || [],
    preferredSkills: job.preferredSkills || job.preferred_skills || [],
    location: job.location || '',
    employmentType: job.employmentType || '',
    status: job.status || 'active',
    candidateCount: Number(job.candidateCount ?? job.candidate_count ?? 0),
    topMatchScore: Number(job.topMatchScore ?? job.top_match_score ?? 0),
    createdAt: job.createdAt || job.created_at,
    updatedAt: job.updatedAt || job.updated_at,
  }
}

function mapRankedCandidate(candidate, uploadsById, jobTerms) {
  const upload = uploadsById.get(candidate.candidate_id)
  const scoreBreakdown = mapCandidateScoreBreakdown(candidate)
  const aiEvaluation =
    candidate.aiEvaluation ||
    candidate.ai_evaluation ||
    scoreBreakdown?.aiEvaluation ||
    scoreBreakdown?.ai_evaluation ||
    null
  const matchedTerms =
    scoreBreakdown?.mustHave?.matchedTerms ||
    scoreBreakdown?.must_have?.matched_terms ||
    scoreBreakdown?.skill_match?.matched_terms ||
    candidate.score_breakdown?.skill_match?.matched_terms ||
    []
  const semanticPreview =
    scoreBreakdown?.semantic_score?.best_matching_chunk_preview ||
    candidate.score_breakdown?.semantic_score?.best_matching_chunk_preview ||
    ''
  const overallExplanation = buildWhyThisScore(candidate)

  return {
    candidateId: candidate.candidate_id,
    name: prettifyFileName(upload?.filename),
    matchScore: candidate.score,
    experienceYears: candidate.score_breakdown?.experience?.detected_years || 0,
    education: '',
    skills: matchedTerms,
    missingSkills: buildMissingSkills(jobTerms, matchedTerms),
    strengths: buildStrengths(candidate),
    summary: semanticPreview || overallExplanation,
    whyThisMatch: overallExplanation,
    whyThisScore: overallExplanation,
    status: normaliseStatus(candidate.status),
    finalScore: candidate.final_score ?? candidate.finalScore ?? candidate.matchScore,
    ruleScore: candidate.rule_score ?? candidate.ruleScore ?? candidate.matchScore,
    semanticScore: candidate.semantic_score ?? candidate.semanticScore ?? null,
    scoringMethod:
      candidate.scoring_method ||
      candidate.scoringMethod ||
      (aiEvaluation ? 'ai-first' : 'legacy'),
    explanation: overallExplanation,
    overallScore: Number(candidate.overallScore ?? candidate.overall_score ?? candidate.score ?? 0),
    coreDomainFit: Number(candidate.coreDomainFit ?? candidate.core_domain_fit ?? 0),
    mustHaveMatch: Number(candidate.mustHaveMatch ?? candidate.must_have_match ?? 0),
    seniorityFit: Number(candidate.seniorityFit ?? candidate.seniority_fit ?? 0),
    responsibilityFit: Number(candidate.responsibilityFit ?? candidate.responsibility_fit ?? 0),
    toolsFit: Number(candidate.toolsFit ?? candidate.tools_fit ?? 0),
    redFlags: Array.isArray(candidate.redFlags) ? candidate.redFlags : Array.isArray(candidate.red_flags) ? candidate.red_flags : [],
    recommendation: candidate.recommendation || '',
    modelRecommendation: candidate.modelRecommendation || candidate.model_recommendation || '',
    matchedTerms,
    bestMatchingChunkPreview: semanticPreview,
    confidence: candidate.confidence,
    scoreBreakdown,
    aiEvaluation: candidate.aiEvaluation || candidate.ai_evaluation || scoreBreakdown?.aiEvaluation || scoreBreakdown?.ai_evaluation || null,
  }
}

function isPlaceholderCandidateName(value) {
  const name = String(value || '').trim().toLowerCase()
  return !name || name === 'candidate' || name === 'unknown'
}

function getCandidateFileName(candidate) {
  return [
    candidate.originalFilename,
    candidate.filename,
    String(candidate.processedS3Key || '').split('/').pop(),
    String(candidate.sourceS3Key || '').split('/').pop(),
    String(candidate.cvS3Key || '').split('/').pop(),
  ].find((value) => String(value || '').trim())
}

function getCandidateDisplayName(candidate) {
  if (!isPlaceholderCandidateName(candidate.fullName)) {
    return String(candidate.fullName).trim()
  }

  return prettifyFileName(getCandidateFileName(candidate))
}

function normaliseStringList(values) {
  if (!Array.isArray(values)) return []
  return values
    .map((value) => (typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''))
    .filter(Boolean)
}

function normaliseWorkHistory(values) {
  if (!Array.isArray(values)) return []
  return values
    .filter((value) => value && typeof value === 'object' && !Array.isArray(value))
    .map((item) => ({
      role: String(item.role || item.title || '').trim(),
      company: String(item.company || item.employer || '').trim(),
      years: Number(item.years || item.durationYears || item.duration_years || 0) || 0,
    }))
    .filter((item) => item.role || item.company || item.years)
}

function mapStoredCandidate(candidate) {
  const name = getCandidateDisplayName(candidate)
  const skills = normaliseStringList(candidate.skills)
  const missingSkills = normaliseStringList(candidate.skillGaps || candidate.missingSkills)
  const educationItems = normaliseStringList(candidate.education)
  const certifications = normaliseStringList(candidate.certifications)
  const workHistory = normaliseWorkHistory(candidate.workHistory)
  const summary = String(candidate.aiSummary || candidate.summary || '').trim()
  const scoreBreakdown = candidate.scoreBreakdown || candidate.score_breakdown || null
  const experienceYears = Number(candidate.totalExperienceYears || 0)
  const matchScore = Number(candidate.matchScore || 0)
  const whyThisScore = buildWhyThisScore(candidate)
  const aiEvaluation = candidate.aiEvaluation || candidate.ai_evaluation || scoreBreakdown?.aiEvaluation || scoreBreakdown?.ai_evaluation || null
  const matchedTerms = normaliseStringList(candidate.matchedTerms)

  return {
    candidateId: candidate.candidateId || candidate.candidate_id,
    name,
    email: candidate.email || '',
    phone: candidate.phone || '',
    currentRole: candidate.currentRole || '',
    matchScore,
    experienceYears,
    education: educationItems.join(', '),
    educationItems,
    certifications,
    workHistory,
    skills,
    missingSkills,
    strengths: summary ? [summary] : [],
    summary,
    whyThisMatch: whyThisScore || summary,
    whyThisScore: whyThisScore || summary,
    status: normaliseStatus(candidate.status || 'ranked'),
    finalScore: matchScore,
    ruleScore: Number(candidate.ruleScore ?? candidate.rule_score ?? matchScore),
    semanticScore: candidate.semanticScore ?? candidate.semantic_score ?? null,
    scoringMethod:
      candidate.scoringMethod ||
      candidate.scoring_method ||
      (aiEvaluation ? 'ai-first' : 'legacy'),
    explanation: whyThisScore || summary,
    overallScore: Number(candidate.overallScore ?? candidate.overall_score ?? matchScore),
    coreDomainFit: Number(candidate.coreDomainFit ?? candidate.core_domain_fit ?? 0),
    mustHaveMatch: Number(candidate.mustHaveMatch ?? candidate.must_have_match ?? 0),
    seniorityFit: Number(candidate.seniorityFit ?? candidate.seniority_fit ?? 0),
    responsibilityFit: Number(candidate.responsibilityFit ?? candidate.responsibility_fit ?? 0),
    toolsFit: Number(candidate.toolsFit ?? candidate.tools_fit ?? 0),
    redFlags: normaliseStringList(candidate.redFlags || candidate.red_flags),
    matchedTerms: matchedTerms.length ? matchedTerms : skills,
    bestMatchingChunkPreview:
      candidate.bestMatchingChunkPreview ||
      candidate.best_matching_chunk_preview ||
      '',
    confidence: null,
    flagged: Boolean(candidate.flagged),
    scoreBreakdown,
    aiEvaluation,
    recommendation:
      candidate.recommendation ||
      aiEvaluation?.recommendation ||
      '',
    modelRecommendation:
      candidate.modelRecommendation ||
      candidate.model_recommendation ||
      aiEvaluation?.modelRecommendation ||
      aiEvaluation?.model_recommendation ||
      '',
    sourceFileName: getCandidateFileName(candidate) || '',
    originalFilename: candidate.originalFilename || candidate.filename || '',
    sourceS3Key: candidate.sourceS3Key || '',
    processedS3Key: candidate.processedS3Key || candidate.cvS3Key || '',
    createdAt: candidate.createdAt || candidate.created_at || '',
    updatedAt: candidate.updatedAt || candidate.updated_at || '',
  }
}

function buildCandidateDedupeKey(candidate) {
  const email = String(candidate.email || '').trim().toLowerCase()
  if (email) {
    return `email:${email}`
  }

  const name = String(candidate.name || '').trim().toLowerCase()
  const role = String(candidate.currentRole || '').trim().toLowerCase()
  const experienceYears = Number(candidate.experienceYears || 0)
  const matchScore = Number(candidate.matchScore || 0)
  const skills = Array.isArray(candidate.skills)
    ? [...candidate.skills].map((skill) => String(skill).trim().toLowerCase()).sort().join('|')
    : ''

  return [name, role, experienceYears, matchScore, skills].join('::')
}

function candidateQualityScore(candidate) {
  let score = 0
  if (!isPlaceholderCandidateName(candidate.name)) score += 5
  if (candidate.email) score += 4
  if (candidate.phone) score += 2
  if (candidate.currentRole) score += 2
  if (candidate.experienceYears) score += 1
  score += Array.isArray(candidate.skills) ? Math.min(candidate.skills.length, 5) : 0
  if (candidate.status === 'shortlisted') score += 2
  if (candidate.status === 'rejected') score += 1
  return score
}

function dedupeCandidates(candidates) {
  const deduped = new Map()

  candidates.forEach((candidate) => {
    const key = buildCandidateDedupeKey(candidate)
    const existing = deduped.get(key)
    if (!existing || candidateQualityScore(candidate) > candidateQualityScore(existing)) {
      deduped.set(key, candidate)
    }
  })

  return [...deduped.values()]
}

function mapAnonymousSessionPayload(payload) {
  const candidates = Array.isArray(payload?.candidates)
    ? dedupeCandidates(payload.candidates.map(mapStoredCandidate))
    : []

  candidates.sort((left, right) => (right.matchScore || 0) - (left.matchScore || 0))

  return {
    sessionId: payload?.sessionId,
    jobId: payload?.jobId,
    jobTitle: payload?.jobTitle || '',
    cvCount: Number(payload?.cvCount || candidates.length || 0),
    maxCVs: Number(payload?.maxCVs || 5),
    claimed: Boolean(payload?.claimed),
    expiresAt: payload?.expiresAt || null,
    candidates,
    totalCandidates: Number(payload?.totalCandidates || candidates.length || 0),
    topMatchScore: Number(payload?.topMatchScore || 0),
  }
}

async function parseResponse(response, fallbackMessage) {
  if (response.ok) {
    if (response.status === 204) return null
    return response.json()
  }

  let message = fallbackMessage
  let payload = null
  try {
    payload = await response.json()
    message = payload?.detail || payload?.error || payload?.message || fallbackMessage
  } catch {
    // Keep fallback message.
  }

  const error = new Error(message)
  error.status = response.status
  error.payload = payload
  error.duplicateCandidate = Boolean(payload?.duplicateCandidate)
  throw error
}

async function request(path, options = {}, fallbackMessage = 'Request failed') {
  let response
  try {
    response = await fetch(`${BASE_URL}${path}`, options)
  } catch (error) {
    throw new Error(error?.message || fallbackMessage)
  }

  return parseResponse(response, fallbackMessage)
}

function buildAuthHeaders(body, headers = {}) {
  const authHeaders = new Headers(headers)
  const token = getToken()
  if (!token) {
    throw new Error('You need to sign in to continue')
  }

  const authorizationValue = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`
  authHeaders.set('Authorization', authorizationValue)
  if (!(body instanceof FormData) && body !== undefined && !authHeaders.has('Content-Type')) {
    authHeaders.set('Content-Type', 'application/json')
  }

  return authHeaders
}

async function authBaseRequest(path, options = {}, fallbackMessage = 'Request failed') {
  await refreshTokenIfNeeded()

  let response
  try {
    response = await fetch(`${LEGACY_BASE_URL}${path}`, {
      ...options,
      headers: buildAuthHeaders(options.body, options.headers),
    })
  } catch (error) {
    throw new Error(error?.message || fallbackMessage)
  }

  if (response.status === 401) {
    clearTokens()
    throw new Error('Your session expired. Please sign in again.')
  }

  return parseResponse(response, fallbackMessage)
}

async function authRequest(path, options = {}, fallbackMessage = 'Request failed') {
  await refreshTokenIfNeeded()

  let response
  try {
    response = await fetch(`${LEGACY_BASE_URL}${path}`, {
      ...options,
      headers: buildAuthHeaders(options.body, options.headers),
    })
  } catch (error) {
    throw new Error(error?.message || fallbackMessage)
  }

  if (response.status === 401) {
    clearTokens()
    throw new Error('Your session expired. Please sign in again.')
  }

  return parseResponse(response, fallbackMessage)
}

async function publicLegacyRequest(path, options = {}, fallbackMessage = 'Request failed') {
  let response
  try {
    response = await fetch(`${LEGACY_BASE_URL}${path}`, options)
  } catch (error) {
    throw new Error(error?.message || fallbackMessage)
  }

  return parseResponse(response, fallbackMessage)
}

function normaliseAnonSession(session) {
  if (!session || typeof session === 'string') {
    return { sessionId: session || '', accessToken: '', claimToken: '' }
  }
  return {
    sessionId: session.sessionId || '',
    accessToken: session.accessToken || session.sessionSecret || '',
    claimToken: session.claimToken || '',
  }
}

function buildAnonHeaders(session, baseHeaders = {}) {
  const { accessToken } = normaliseAnonSession(session)
  if (!accessToken) {
    throw new Error('Anonymous session expired. Start a new upload.')
  }
  return {
    ...baseHeaders,
    'X-Anonymous-Session-Token': accessToken,
  }
}

function buildAnonClaimHeaders(session, baseHeaders = {}) {
  const { claimToken } = normaliseAnonSession(session)
  if (!claimToken) {
    throw new Error('Anonymous session expired. Start a new upload.')
  }
  return {
    ...buildAnonHeaders(session, baseHeaders),
    'X-Anonymous-Claim-Token': claimToken,
  }
}

function getJobsFromPayload(payload) {
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.jobs)) return payload.jobs
  return []
}

function unwrapJobPayload(payload) {
  return payload?.job || payload
}

function extractJobId(payload) {
  return payload?.job_id || payload?.jobId || payload?.job?.jobId || payload?.job?.job_id || null
}

function stageUpload(fileName, contentType) {
  const token =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`

  stagedUploads.set(token, {
    fileName,
    contentType,
    file: null,
  })

  return {
    uploadUrl: `memory://${token}`,
    s3Key: token,
  }
}

function rememberUploadSession(s3Key, uploadId, fileName, contentType) {
  stagedUploads.set(s3Key, {
    uploadId,
    fileName,
    contentType,
    file: null,
  })
}

function consumeUploadSession(s3Key) {
  const entry = stagedUploads.get(s3Key)
  if (!entry?.uploadId) {
    throw new Error('Upload session expired before processing started')
  }

  stagedUploads.delete(s3Key)
  return entry.uploadId
}

function getStagedUploadToken(uploadUrl) {
  return String(uploadUrl || '').replace(/^memory:\/\//, '')
}

function consumeStagedUpload(token) {
  const entry = stagedUploads.get(token)
  if (!entry?.file) {
    throw new Error('Selected file is no longer available for upload')
  }

  stagedUploads.delete(token)
  return entry.file
}

async function uploadCvDirect(jobId, file) {
  const formData = new FormData()
  formData.append('file', file)

  return request(
    `/jobs/${encodeURIComponent(jobId)}/upload-cv`,
    {
      method: 'POST',
      body: formData,
    },
    `Failed to upload ${file.name}`
  )
}

async function getAuthenticatedUploadUrl(jobId, fileName, contentType) {
  const payload = await authBaseRequest(
    `/jobs/${encodeURIComponent(jobId)}/uploads/presign`,
    {
      method: 'POST',
      body: JSON.stringify({
        filename: fileName,
        contentType,
      }),
    },
    `Failed to prepare upload for ${fileName}`
  )

  rememberUploadSession(payload.s3Key, payload.uploadId, fileName, contentType)

  return {
    uploadUrl: payload.uploadUrl,
    s3Key: payload.s3Key,
    uploadId: payload.uploadId,
  }
}

async function processAuthenticatedUpload(jobId, s3Key, fileName, locale) {
  const uploadId = consumeUploadSession(s3Key)
  const payload = await authBaseRequest(
    `/jobs/${encodeURIComponent(jobId)}/uploads/process`,
    {
      method: 'POST',
      body: JSON.stringify({ uploadId, locale: resolveLocale(locale) }),
    },
    `Failed to process ${fileName || 'uploaded CV'}`
  )

  return {
    candidateId: payload.candidateId,
    uploadId: payload.uploadId,
    status: payload.status,
    name: fileName ? prettifyFileName(fileName) : undefined,
    matchScore: undefined,
  }
}

async function getJobRecord(jobId, { authenticated = false } = {}) {
  const requester = authenticated ? authBaseRequest : request
  const payload = await requester(
    `/jobs/${encodeURIComponent(jobId)}`,
    {},
    'Failed to load job'
  )
  return unwrapJobPayload(payload)
}

async function getUploads(jobId, { authenticated = false } = {}) {
  const requester = authenticated ? authBaseRequest : request
  const payload = await requester(
    `/jobs/${encodeURIComponent(jobId)}/uploads`,
    {},
    'Failed to load uploaded CVs'
  )
  return Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.uploads)
    ? payload.uploads
    : []
}

async function getCandidatesPayload(jobId, { authenticated = false } = {}) {
  const requester = authenticated ? authBaseRequest : request
  const payload = await requester(
    `/jobs/${encodeURIComponent(jobId)}/candidates`,
    {},
    'Failed to load candidates'
  )
  return Array.isArray(payload?.candidates)
    ? dedupeCandidates(payload.candidates.map(mapStoredCandidate))
    : []
}

async function getRankingPayload(jobId, { authenticated = false } = {}) {
  const requester = authenticated ? authBaseRequest : request
  return requester(
    `/jobs/${encodeURIComponent(jobId)}/rank`,
    { method: 'POST' },
    'Failed to rank candidates'
  )
}

async function getRankedView(jobId, status, { authenticated = false } = {}) {
  const [job, uploads, ranking] = await Promise.all([
    getJobRecord(jobId, { authenticated }),
    getUploads(jobId, { authenticated }),
    getRankingPayload(jobId, { authenticated }),
  ])

  const uploadsById = new Map(uploads.map((upload) => [upload.upload_id, upload]))
  const jobTerms = Array.isArray(ranking?.job_terms) ? ranking.job_terms : []
  let candidates = Array.isArray(ranking?.candidates)
    ? ranking.candidates.map((candidate) => mapRankedCandidate(candidate, uploadsById, jobTerms))
    : []

  if (status && status !== 'all') {
    candidates = candidates.filter((candidate) => candidate.status === status)
  }

  candidates.sort((left, right) => (right.matchScore || 0) - (left.matchScore || 0))

  return {
    jobId: job.job_id || job.jobId,
    jobTitle: job.title || job.jobTitle,
    jobDescription: job.description || job.jobDescription,
    candidates,
  }
}

async function getStoredCandidatesView(jobId, status, { authenticated = false } = {}) {
  const [job, candidatesPayload] = await Promise.all([
    getJobRecord(jobId, { authenticated }),
    getCandidatesPayload(jobId, { authenticated }),
  ])

  let candidates = [...candidatesPayload]

  if (status && status !== 'all') {
    candidates = candidates.filter((candidate) => candidate.status === status)
  }

  candidates.sort((left, right) => (right.matchScore || 0) - (left.matchScore || 0))

  return {
    jobId: job.job_id || job.jobId,
    jobTitle: job.title || job.jobTitle,
    jobDescription: job.description || job.jobDescription,
    candidates,
  }
}

function buildCsv(rows) {
  return rows
    .map((row) =>
      row
        .map((value) => `\"${String(value ?? "").replace(/\"/g, '\"\"')}\"`)
        .join(',')
    )
    .join('\n')
}

async function listJobRecords({ authenticated = false } = {}) {
  const requester = authenticated ? authBaseRequest : request
  const payload = await requester('/jobs', {}, 'Failed to load jobs')
  return getJobsFromPayload(payload)
}

async function buildJobSummary(job, { authenticated = false } = {}) {
  const mappedJob = mapJobRecord(job)
  const existingCandidateCount = job?.candidateCount ?? job?.candidate_count ?? job?.cvCount
  const existingTopMatchScore = job?.topMatchScore ?? job?.top_match_score
  const jobKey = mappedJob.jobId || job?.jobId || job?.job_id

  if (!jobKey) {
    return {
      ...mappedJob,
      candidateCount: Number(existingCandidateCount ?? 0),
      topMatchScore: Number(existingTopMatchScore ?? 0),
    }
  }

  if (existingCandidateCount !== undefined || existingTopMatchScore !== undefined) {
    return {
      ...mappedJob,
      candidateCount: Number(existingCandidateCount ?? 0),
      topMatchScore: Number(existingTopMatchScore ?? 0),
    }
  }

  if (authenticated) {
    return {
      ...mappedJob,
      candidateCount: 0,
      topMatchScore: 0,
    }
  }

  try {
    const uploads = await getUploads(jobKey, { authenticated })
    let topMatchScore = 0

    if (uploads.length > 0) {
      try {
        const ranking = await getRankingPayload(jobKey, { authenticated })
        const scores = Array.isArray(ranking?.candidates)
          ? ranking.candidates.map((candidate) => candidate.score || 0)
          : []
        topMatchScore = scores.length ? Math.max(...scores) : 0
      } catch {
        // Keep job cards usable even if ranking cannot be recomputed yet.
      }
    }

    return {
      ...mappedJob,
      candidateCount: uploads.length,
      topMatchScore,
    }
  } catch {
    return {
      ...mappedJob,
      candidateCount: 0,
      topMatchScore: 0,
    }
  }
}

async function createJobRecord(jobData, { authenticated = false } = {}) {
  const title = String(jobData?.title || jobData?.jobTitle || '').trim() || 'Uploaded role'
  const description = buildJobDescription(jobData)
  const requiredSkills = [
    ...(jobData?.requiredSkills || jobData?.required_skills || []),
    ...splitRequirementText(jobData?.requirements),
  ]

  const requester = authenticated ? authBaseRequest : request
  const payload = await requester(
    '/jobs',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title,
        description,
        required_skills: requiredSkills,
        preferred_skills: jobData?.preferredSkills || jobData?.preferred_skills || [],
        target_experience_years:
          jobData?.targetExperienceYears ?? jobData?.target_experience_years ?? null,
      }),
    },
    'Failed to create job'
  )

  const jobId = extractJobId(payload)
  return jobId ? { ...payload, job_id: jobId } : payload
}

export const authApi = {
  createOrg: (payload) =>
    authRequest(
      '/org/create',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      'Failed to create organisation'
    ),
  getOrg: () => authRequest('/org', {}, 'Failed to load organisation'),
  updateOrg: (updates) =>
    authRequest(
      '/org',
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      },
      'Failed to update organisation'
    ),
  getUsage: () => authRequest('/org/usage', {}, 'Failed to load usage'),
}

export const jobsApi = {
  list: async (status = 'active') => {
    if (status && status !== 'active' && status !== 'all') {
      return { jobs: [] }
    }

    const jobs = await listJobRecords({ authenticated: true })
    return {
      jobs: await Promise.all(
        jobs.map((job) => buildJobSummary(job, { authenticated: true }))
      ),
    }
  },

  get: async (jobId) => mapJobRecord(await getJobRecord(jobId, { authenticated: true })),

  create: async (data) => {
    const payload = await createJobRecord(data, { authenticated: true })
    if (!payload?.job_id) {
      throw new Error('Failed to create job')
    }

    const createdJob = payload?.job
      ? mapJobRecord(payload.job)
      : mapJobRecord(await getJobRecord(payload.job_id, { authenticated: true }))

    return { jobId: payload.job_id, job: createdJob }
  },

  update: async (jobId, data) => {
    if (data?.status && Object.keys(data).every((key) => key === 'status')) {
      throw new Error('The V2 backend does not support archived or closed job states.')
    }

    const title = data?.title ? String(data.title).trim() : undefined
    const description =
      data?.description === undefined
        ? undefined
        : buildJobDescription({
            title: title || 'Uploaded role',
            description: data.description,
            requirements: data?.requirements,
            location: data?.location,
            employmentType: data?.employmentType,
          })
    const requiredSkills = [
      ...(data?.requiredSkills ?? data?.required_skills ?? []),
      ...splitRequirementText(data?.requirements),
    ]

    const response = await authBaseRequest(
      `/jobs/${encodeURIComponent(jobId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          description,
          requirements: data?.requirements,
          required_skills: requiredSkills.length ? requiredSkills : undefined,
          preferred_skills: data?.preferredSkills ?? data?.preferred_skills,
          target_experience_years:
            data?.targetExperienceYears ?? data?.target_experience_years,
          location: data?.location,
          employmentType: data?.employmentType,
          locale: resolveLocale(data?.locale),
          rescoreCandidates: Boolean(
            data?.rescoreCandidates || data?.reprocessCandidates
          ),
        }),
      },
      'Failed to update job'
    )

    return {
      job: mapJobRecord(await getJobRecord(jobId, { authenticated: true })),
      rescore: response?.rescore || null,
    }
  },

  delete: (jobId) =>
    authBaseRequest(
      `/jobs/${encodeURIComponent(jobId)}`,
      { method: 'DELETE' },
      'Failed to delete job'
    ),
}

export const candidatesApi = {
  list: async (jobId) => {
    const data = await getStoredCandidatesView(jobId, undefined, { authenticated: true })
    return { candidates: data.candidates }
  },

  getDetail: async (jobId, candidateId) => {
    try {
      return mapStoredCandidate(
        await authBaseRequest(
          `/jobs/${encodeURIComponent(jobId)}/candidates/${encodeURIComponent(candidateId)}`,
          {},
          'Failed to load candidate detail'
        )
      )
    } catch (error) {
      const data = await getStoredCandidatesView(jobId, undefined, { authenticated: true })
      const candidate = data.candidates.find((item) => item.candidateId === candidateId)
      if (candidate) {
        return candidate
      }
      throw error
    }
  },

  updateStatus: async (jobId, candidateId, status) => {
    const payload = await authRequest(
      `/jobs/${encodeURIComponent(jobId)}/candidates/${encodeURIComponent(candidateId)}/status`,
      {
        method: 'PUT',
        body: JSON.stringify({ status }),
      },
      'Failed to update candidate status'
    )

    return { status: normaliseStatus(payload?.status || status) }
  },

  compare: async (jobId, candidateIds) => {
    const data = await getStoredCandidatesView(jobId, undefined, { authenticated: true })
    return {
      candidates: data.candidates.filter((candidate) => candidateIds.includes(candidate.candidateId)),
    }
  },
}

export const matchesApi = {
  getRanked: (jobId, status) =>
    getStoredCandidatesView(jobId, status, { authenticated: true }),
}

export const uploadApi = {
  getUploadUrl: async (jobId, fileName, contentType) =>
    getAuthenticatedUploadUrl(jobId, fileName, contentType),
  uploadCV: async (jobId, file, locale) => {
    const { uploadUrl, s3Key } = await getAuthenticatedUploadUrl(jobId, file.name, file.type)
    await uploadFileToS3(uploadUrl, file)
    return processAuthenticatedUpload(jobId, s3Key, file.name, locale)
  },

  parseCV: async (jobId, s3Key, fileName, locale) => processAuthenticatedUpload(jobId, s3Key, fileName, locale),
}

export const anonApi = {
  createSession: (jobTitle, jobDescription) =>
    publicLegacyRequest(
      '/anonymous/session',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobTitle: jobTitle || 'Uploaded role',
          jobDescription: jobDescription || '',
        }),
      },
      'Failed to create anonymous session'
    ),

  getSession: async (session) =>
    mapAnonymousSessionPayload(
      await publicLegacyRequest(
        `/anonymous/session/${encodeURIComponent(normaliseAnonSession(session).sessionId)}`,
        { headers: buildAnonHeaders(session) },
        'Failed to load anonymous session'
      )
    ),

  rankCandidates: async (session) =>
    mapAnonymousSessionPayload(
      await publicLegacyRequest(
        `/anonymous/session/${encodeURIComponent(normaliseAnonSession(session).sessionId)}`,
        { headers: buildAnonHeaders(session) },
        'Failed to load anonymous session'
      )
    ),

  uploadCV: async (session, file, locale) => {
    const { sessionId } = normaliseAnonSession(session)
    const upload = await publicLegacyRequest(
      '/anonymous/upload-url',
      {
        method: 'POST',
        headers: buildAnonHeaders(session, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          sessionId,
          fileName: file.name,
          contentType: file.type || 'application/pdf',
        }),
      },
      `Failed to prepare upload for ${file.name}`
    )

    await uploadFileToS3(upload.uploadUrl, file)

    return publicLegacyRequest(
      '/anonymous/parse',
      {
        method: 'POST',
        headers: buildAnonHeaders(session, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          sessionId,
          uploadId: upload.uploadId,
          s3Key: upload.s3Key,
          fileName: file.name,
          locale: resolveLocale(locale),
        }),
      },
      `Failed to process ${file.name}`
    )
  },

  getUploadUrl: (session, fileName, contentType) => {
    const { sessionId } = normaliseAnonSession(session)
    return publicLegacyRequest(
      '/anonymous/upload-url',
      {
        method: 'POST',
        headers: buildAnonHeaders(session, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          sessionId,
          fileName,
          contentType,
        }),
      },
      `Failed to prepare upload for ${fileName}`
    )
  },

  parseCV: (session, upload, fileName, locale) => {
    const { sessionId } = normaliseAnonSession(session)
    const uploadPayload = typeof upload === 'string' ? { s3Key: upload } : upload || {}
    return publicLegacyRequest(
      '/anonymous/parse',
      {
        method: 'POST',
        headers: buildAnonHeaders(session, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          sessionId,
          uploadId: uploadPayload.uploadId,
          s3Key: uploadPayload.s3Key,
          fileName,
          locale: resolveLocale(locale),
        }),
      },
      `Failed to process ${fileName || 'uploaded CV'}`
    )
  },

  claimSession: async (session) => {
    const { sessionId } = normaliseAnonSession(session)
    if (!sessionId) {
      return { success: false }
    }

    return authRequest(
      '/anonymous/claim',
      {
        method: 'POST',
        headers: buildAnonClaimHeaders(session),
        body: JSON.stringify({ sessionId }),
      },
      'Failed to claim anonymous session'
    )
  },
}

export const teamApi = {
  listMembers: () => authRequest('/team/members', {}, 'Failed to load team members'),
  inviteMember: (email, role) =>
    authRequest(
      '/team/invite',
      {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      },
      'Failed to invite team member'
    ),
  verifyInvite: (token, inviteId, orgId) =>
    publicLegacyRequest(
      `/team/invite/verify?token=${encodeURIComponent(token)}&inviteId=${encodeURIComponent(
        inviteId
      )}&orgId=${encodeURIComponent(orgId)}`,
      {},
      'Failed to verify invitation'
    ),
  acceptInvite: (token, inviteId, orgId) =>
    authRequest(
      '/team/accept',
      {
        method: 'POST',
        body: JSON.stringify({ token, inviteId, orgId }),
      },
      'Failed to accept invitation'
    ),
  updateRole: (userId, role) =>
    authRequest(
      `/team/members/${encodeURIComponent(userId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ role }),
      },
      'Failed to update member role'
    ),
  removeMember: (userId) =>
    authRequest(
      `/team/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
      'Failed to remove team member'
    ),
}

export const stripeApi = {
  createCheckout: async (plan) => {
    const data = await authRequest(
      '/stripe-checkout',
      {
        method: 'POST',
        body: JSON.stringify({ plan }),
      },
      'Failed to create checkout session'
    )

    return {
      ...data,
      checkoutUrl: data?.checkoutUrl || data?.url || data?.redirectUrl || '',
      redirectUrl: data?.redirectUrl || data?.url || data?.checkoutUrl || '',
    }
  },
  createPortal: () =>
    authRequest(
      '/stripe-portal',
      { method: 'POST' },
      'Failed to open billing portal'
    ),
  cancelSubscription: () =>
    authRequest(
      '/stripe-cancel',
      { method: 'POST' },
      'Failed to cancel subscription'
    ),
}

export const exportApi = {
  generate: async (jobId, format = 'CSV', options = {}) => {
    const data = await getStoredCandidatesView(jobId, undefined, { authenticated: true })
    const scope = String(options?.scope || 'selected').trim().toLowerCase()
    const candidateIds = new Set(
      Array.isArray(options?.candidateIds) ? options.candidateIds.filter(Boolean) : []
    )
    let exportCandidates = [...data.candidates]

    if (scope === 'selected') {
      exportCandidates = candidateIds.size
        ? exportCandidates.filter((candidate) => candidateIds.has(candidate.candidateId))
        : []
    } else if (scope === 'shortlisted') {
      exportCandidates = exportCandidates.filter((candidate) => candidate.status === 'shortlisted')
    }

    const rows = [
      [
        'Candidate',
        'Status',
        'Match Score',
        'Final Score',
        'Rule Score',
        'Semantic Score',
        'Scoring Method',
        'Explanation',
        'Matched Terms',
        'Best Matching Chunk Preview',
      ],
    ]

    exportCandidates.forEach((candidate) => {
      rows.push([
        candidate.name,
        candidate.status,
        candidate.matchScore,
        candidate.finalScore,
        candidate.ruleScore,
        candidate.semanticScore ?? '',
        candidate.scoringMethod,
        candidate.explanation,
        candidate.matchedTerms.join(', '),
        candidate.bestMatchingChunkPreview,
      ])
    })

    const csv = buildCsv(rows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    return {
      candidateCount: exportCandidates.length,
      totalCandidateCount: data.candidates.length,
      scope,
      format,
      downloadUrl: URL.createObjectURL(blob),
    }
  },
}

export const adminApi = {
  listUsers: () => authRequest('/admin/users', {}, 'Failed to load users'),
  createUser: (email, plan) =>
    authRequest(
      '/admin/users',
      {
        method: 'POST',
        body: JSON.stringify({ email, plan }),
      },
      'Failed to create user'
    ),
  updateUser: (username, updates) =>
    authRequest(
      `/admin/users/${encodeURIComponent(username)}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      },
      'Failed to update user'
    ),
  deleteUser: (username) =>
    authRequest(
      `/admin/users/${encodeURIComponent(username)}`,
      { method: 'DELETE' },
      'Failed to delete user'
    ),
}

export const settingsApi = {
  getNotifications: () =>
    authRequest(
      '/settings/notifications',
      {},
      'Failed to load notification settings'
    ),
  updateNotifications: (emailNotifications) =>
    authRequest(
      '/settings/notifications',
      {
        method: 'PUT',
        body: JSON.stringify({ emailNotifications }),
      },
      'Failed to update notification settings'
    ),
}

export async function uploadFileToS3(presignedUrl, file) {
  if (String(presignedUrl || '').startsWith('memory://')) {
    const token = getStagedUploadToken(presignedUrl)
    const staged = stagedUploads.get(token)
    if (!staged) {
      throw new Error('Upload session expired before the file was attached')
    }

    staged.file = file
    stagedUploads.set(token, staged)
    return true
  }

  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  })

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.statusText}`)
  }

  return true
}

export default {
  jobsApi,
  candidatesApi,
  matchesApi,
  uploadApi,
  anonApi,
  exportApi,
}
